import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { fmtDate } from "@/lib/time";
import { PageHeader, Field } from "@/components/Ui";
import { useAuth } from "@/context/AuthContext";
import {
    Zap, Wallet, RefreshCw, Bell, FileText, BookOpen,
    ShieldCheck, AlertTriangle, CheckCircle2, XCircle,
    ChevronDown, ChevronUp, CreditCard
} from "lucide-react";

// ── Helpers ──────────────────────────────────────────────────────────────────

function Toggle({ checked, onChange, disabled, testid }) {
    return (
        <button
            onClick={() => !disabled && onChange(!checked)}
            data-testid={testid}
            disabled={disabled}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none
                ${disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}
                ${checked ? "bg-green-500" : "bg-muted-foreground/40"}`}
        >
            <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform
                ${checked ? "translate-x-6" : "translate-x-1"}`} />
        </button>
    );
}

function SectionCard({ icon: Icon, title, badge, children, testid }) {
    const [open, setOpen] = useState(true);
    return (
        <div className="border border-border bg-card" data-testid={testid}>
            <button
                className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-muted/20 transition-colors"
                onClick={() => setOpen(o => !o)}
            >
                <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4 text-primary" />
                    <span className="text-sm font-medium text-foreground">{title}</span>
                    {badge && <span className="text-[10px] px-2 py-0.5 bg-muted text-muted-foreground uppercase tracking-wide">{badge}</span>}
                </div>
                {open ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
            </button>
            {open && <div className="px-4 pb-4 space-y-3">{children}</div>}
        </div>
    );
}

function AutoRow({ label, hint, checked, onChange, disabled, testid }) {
    return (
        <div className="flex items-center justify-between py-2 border-b border-border/40 last:border-0">
            <div>
                <div className="text-sm text-foreground">{label}</div>
                {hint && <div className="text-xs text-muted-foreground mt-0.5">{hint}</div>}
            </div>
            <Toggle checked={checked} onChange={onChange} disabled={disabled} testid={testid} />
        </div>
    );
}

// ── Mandate Card ──────────────────────────────────────────────────────────────

function MandateCard({ onMandateChange }) {
    const [mandate, setMandate] = useState(null);
    const [loading, setLoading] = useState(true);
    const [settingUp, setSettingUp] = useState(false);
    const [amount, setAmount] = useState(500);
    const [mandateType, setMandateType] = useState("upi");

    const loadMandate = useCallback(async () => {
        try { const { data } = await api.get("/mandate"); setMandate(data); onMandateChange?.(data); }
        catch { setMandate({ status: "none" }); }
        finally { setLoading(false); }
    }, [onMandateChange]);

    useEffect(() => { loadMandate(); }, [loadMandate]);

    const setupMandate = async () => {
        setSettingUp(true);
        try {
            // Ensure Razorpay customer exists
            await api.post("/mandate/customer/ensure");
            // Create order for mandate setup
            const { data: order } = await api.post("/mandate/setup/start", {
                amount: Number(amount), mandate_type: mandateType, mandate_only: amount === 1
            });

            // Load Razorpay script
            await new Promise((res, rej) => {
                if (window.Razorpay) return res();
                const s = document.createElement("script");
                s.src = "https://checkout.razorpay.com/v1/checkout.js";
                s.onload = res; s.onerror = rej;
                document.head.appendChild(s);
            });

            await new Promise((resolve, reject) => {
                const rzp = new window.Razorpay({
                    key: order.key_id,
                    amount: order.amount,
                    currency: order.currency,
                    order_id: order.order_id,
                    customer_id: order.customer_id,
                    recurring: 1,
                    name: "Law OS",
                    description: amount === 1
                        ? "Auto-Pay Mandate Setup (₹1 auth — refunded to wallet)"
                        : "Wallet Top-up + Auto-Pay Mandate Setup",
                    prefill: {},
                    handler: async function(response) {
                        try {
                            await api.post("/mandate/setup/verify", {
                                razorpay_order_id:   response.razorpay_order_id,
                                razorpay_payment_id: response.razorpay_payment_id,
                                razorpay_signature:  response.razorpay_signature,
                            });
                            toast.success(
                                amount === 1
                                    ? "Mandate active — ₹1 credited to wallet as auth credit"
                                    : `Mandate active — ₹${amount} credited to your wallet`
                            );
                            await loadMandate();
                            resolve();
                        } catch (e) { toast.error(apiError(e)); reject(e); }
                    },
                    modal: { ondismiss: () => reject(new Error("dismissed")) },
                });
                rzp.open();
            });
        } catch (e) {
            if (e?.message !== "dismissed") toast.error(apiError(e));
        } finally { setSettingUp(false); }
    };

    const cancelMandate = async () => {
        if (!window.confirm("Cancel Auto-Pay mandate? Auto-recharge will be disabled until you set up a new mandate.")) return;
        try {
            await api.delete("/mandate");
            toast.success("Mandate cancelled");
            loadMandate();
        } catch (e) { toast.error(apiError(e)); }
    };

    if (loading) return <div className="text-xs text-muted-foreground p-4">Loading mandate…</div>;

    const isActive = mandate?.status === "active";
    const isPending = mandate?.status === "pending_setup";

    return (
        <SectionCard icon={CreditCard} title="Auto-Pay Mandate" badge="UPI · e-NACH" testid="mandate-card">
            {/* Status banner */}
            <div className={`flex items-center gap-2 px-3 py-2 rounded text-xs
                ${isActive ? "bg-green-500/10 border border-green-500/30 text-green-400"
                : isPending ? "bg-amber-500/10 border border-amber-500/30 text-amber-400"
                : "bg-red-500/10 border border-red-500/30 text-red-400"}`}>
                {isActive ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
                <span>
                    {isActive ? `Mandate active · ${mandate?.mandate_type?.toUpperCase() || "UPI"} AutoPay`
                    : isPending ? "Mandate authorization pending"
                    : "No active mandate — set up UPI AutoPay to enable auto-recharge"}
                </span>
            </div>

            {isActive ? (
                <div className="space-y-2">
                    <div className="text-xs text-muted-foreground font-mono">
                        Activated: {mandate?.activated_at ? fmtDate(mandate.activated_at) : "—"}
                    </div>
                    <button onClick={cancelMandate} data-testid="mandate-cancel"
                        className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1 mt-1">
                        <XCircle className="w-3 h-3" /> Cancel mandate
                    </button>
                </div>
            ) : (
                <div className="space-y-3 pt-1">
                    <div className="text-xs text-muted-foreground">
                        Set up UPI AutoPay once — used for wallet auto-recharge, subscription renewal, and all future platform charges.
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Mandate type">
                            <select value={mandateType} onChange={e => setMandateType(e.target.value)}
                                data-testid="mandate-type-select"
                                className="w-full bg-background border border-border px-3 py-2 text-sm text-foreground">
                                <option value="upi">UPI AutoPay (recommended)</option>
                                <option value="nach">e-NACH (bank account)</option>
                            </select>
                        </Field>
                        <Field label="First wallet top-up (₹)">
                            <div className="flex flex-wrap gap-2">
                                <button onClick={() => setAmount(1)}
                                    className={`px-2 py-1 text-xs border transition-colors
                                        ${amount === 1 ? "border-primary text-primary bg-primary/5" : "border-border text-muted-foreground hover:border-primary/50"}`}>
                                    ₹1 mandate only
                                </button>
                                {[250, 500, 1000, 2000].map(v => (
                                    <button key={v} onClick={() => setAmount(v)}
                                        className={`px-2 py-1 text-xs border transition-colors
                                            ${amount === v ? "border-primary text-primary bg-primary/5" : "border-border text-muted-foreground hover:border-primary/50"}`}>
                                        ₹{v}
                                    </button>
                                ))}
                            </div>
                            {amount === 1 && (
                                <div className="text-[11px] text-muted-foreground mt-1">
                                    ₹1 auth charge — credited back to your wallet immediately. Set up mandate now, top up separately later.
                                </div>
                            )}
                        </Field>
                    </div>
                    <button onClick={setupMandate} disabled={settingUp} data-testid="mandate-setup-btn"
                        className="px-4 py-2 bg-primary text-primary-foreground text-[10px] uppercase tracking-[0.16em] hover:opacity-90 disabled:opacity-50">
                        {settingUp ? "Opening checkout…" : "Set up Auto-Pay mandate →"}
                    </button>
                </div>
            )}

            {/* Recharge history */}
            <RechargeHistory />
        </SectionCard>
    );
}

function RechargeHistory() {
    const [history, setHistory] = useState([]);
    useEffect(() => {
        api.get("/mandate/recharge-history").then(r => setHistory(r.data)).catch(() => {});
    }, []);
    if (!history.length) return null;
    return (
        <div className="mt-2">
            <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-1">Auto-recharge history</div>
            <div className="space-y-1">
                {history.slice(0, 5).map(h => (
                    <div key={h.id} className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">{fmtDate(h.created_at)}</span>
                        <span className="font-mono text-foreground">₹{h.amount}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded
                            ${h.status === "success" ? "bg-green-500/15 text-green-400"
                            : h.status === "failed" ? "bg-red-500/15 text-red-400"
                            : "bg-amber-500/15 text-amber-400"}`}>
                            {h.status}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
}

// ── Wallet Auto-Recharge Settings ─────────────────────────────────────────────

function WalletAutoRechargeCard({ cfg, onChange, mandateActive }) {
    const wallet = cfg?.billing?.wallet || {};
    const set = (key, val) => onChange({ billing: { wallet: { [key]: val } } });

    return (
        <SectionCard icon={Wallet} title="Wallet Auto-Recharge" testid="wallet-autorecharge-card">
            {!mandateActive && (
                <div className="text-xs bg-amber-500/10 border border-amber-500/30 px-3 py-2 text-amber-400 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Set up an Auto-Pay mandate above to enable auto-recharge.
                </div>
            )}
            <AutoRow
                label="Auto-recharge when balance is low"
                hint={`When wallet balance drops below ₹${wallet.threshold || 100}, automatically recharge ₹${wallet.recharge_amount || 500}`}
                checked={!!wallet.auto_recharge_enabled}
                onChange={v => set("auto_recharge_enabled", v)}
                testid="autorecharge-toggle"
            />
            {wallet.disabled_reason && (
                <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 px-3 py-2">
                    Auto-recharge was disabled: {wallet.disabled_reason}
                </div>
            )}
            <div className={`grid grid-cols-2 gap-4 pt-1 transition-opacity ${wallet.auto_recharge_enabled ? "opacity-100" : "opacity-50"}`}>
                <Field label="Recharge trigger threshold (₹)" hint="Min ₹50, Max ₹1,000">
                    <input type="number" value={wallet.threshold || 100} min={50} max={1000}
                        onChange={e => set("threshold", parseFloat(e.target.value))}
                        data-testid="autorecharge-threshold"
                        className="w-full bg-background border border-border px-3 py-2 text-sm text-foreground" />
                </Field>
                <Field label="Auto-recharge amount (₹)" hint="Min ₹250, Max ₹5,000">
                    <input type="number" value={wallet.recharge_amount || 500} min={250} max={5000}
                        onChange={e => set("recharge_amount", parseFloat(e.target.value))}
                        data-testid="autorecharge-amount"
                        className="w-full bg-background border border-border px-3 py-2 text-sm text-foreground" />
                </Field>
                <Field label="Daily cap (₹)">
                    <input type="number" value={cfg?.billing?.daily_cap || 5000} min={250} max={20000}
                        onChange={e => onChange({ billing: { daily_cap: parseFloat(e.target.value) } })}
                        data-testid="autorecharge-daily-cap"
                        className="w-full bg-background border border-border px-3 py-2 text-sm text-foreground" />
                </Field>
                <Field label="Monthly cap (₹)">
                    <input type="number" value={cfg?.billing?.monthly_cap || 20000} min={1000} max={100000}
                        onChange={e => onChange({ billing: { monthly_cap: parseFloat(e.target.value) } })}
                        data-testid="autorecharge-monthly-cap"
                        className="w-full bg-background border border-border px-3 py-2 text-sm text-foreground" />
                </Field>
            </div>
        </SectionCard>
    );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function BillingAutomation() {
    const { user } = useAuth();
    const [cfg, setCfg] = useState(null);
    const [saving, setSaving] = useState(false);
    const [mandateActive, setMandateActive] = useState(false);

    const load = useCallback(() => {
        api.get("/settings/billing-automation").then(r => setCfg(r.data)).catch(() => {});
    }, []);

    useEffect(() => { load(); }, [load]);

    const patch = async (delta) => {
        setSaving(true);
        try {
            const { data } = await api.patch("/settings/billing-automation", delta);
            setCfg(data);
            toast.success("Saved");
        } catch (e) { toast.error(apiError(e)); }
        finally { setSaving(false); }
    };

    // Deep merge local state for live preview before save
    const localPatch = (delta) => {
        setCfg(prev => deepMerge(prev || {}, delta));
    };

    const saveAll = async () => {
        if (!cfg) return;
        await patch(cfg);
    };

    if (!cfg) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;

    const comm = cfg.communications || {};
    const billing = cfg.billing || {};
    const sub = billing.subscriptions || {};
    const inv = billing.invoicing || {};
    const col = billing.collections || {};
    const comp = billing.compliance || {};

    return (
        <>
            <PageHeader title="Billing Automation" subtitle="Configure automated billing workflows. All billing automations are disabled by default." />

            <div className="max-w-3xl space-y-4">

                {/* Books — always ON */}
                <div className="flex items-center gap-3 px-4 py-3 border border-green-500/30 bg-green-500/5">
                    <BookOpen className="w-4 h-4 text-green-400" />
                    <div>
                        <div className="text-sm font-medium text-foreground">Books auto-post</div>
                        <div className="text-xs text-muted-foreground">Every billing event posts a double-entry journal automatically. Always enabled — cannot be disabled.</div>
                    </div>
                    <div className="ml-auto">
                        <span className="text-xs text-green-400 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Always ON</span>
                    </div>
                </div>

                {/* Mandate Setup */}
                <MandateCard onMandateChange={m => setMandateActive(m?.status === "active")} />

                {/* Wallet Auto-Recharge */}
                <WalletAutoRechargeCard cfg={cfg} onChange={delta => { localPatch(delta); }} mandateActive={mandateActive} />

                {/* Communications */}
                <SectionCard icon={Bell} title="Communications" badge="on by default" testid="comms-section">
                    <div className="text-xs text-muted-foreground pb-1">Notification channels for billing events. These are ON by default.</div>
                    <AutoRow label="Invoice email to client" hint="PDF invoice sent on creation" checked={!!comm.invoice_email} onChange={v => localPatch({ communications: { invoice_email: v } })} testid="comm-invoice-email" />
                    <AutoRow label="Payment receipt" hint="Email + WhatsApp on payment received" checked={!!comm.payment_receipt} onChange={v => localPatch({ communications: { payment_receipt: v } })} testid="comm-payment-receipt" />
                    <AutoRow label="Low balance alert" hint="Alert before auto-recharge triggers" checked={!!comm.low_balance_alert} onChange={v => localPatch({ communications: { low_balance_alert: v } })} testid="comm-low-balance" />
                    <AutoRow label="Mandate debit notification" hint="Before and after every auto-debit" checked={!!comm.mandate_debit_notification} onChange={v => localPatch({ communications: { mandate_debit_notification: v } })} testid="comm-mandate-notify" />
                    <AutoRow label="Payment reminder sequence" hint="D+7, D+15, D+30 reminders to clients" checked={!!comm.payment_reminders} onChange={v => localPatch({ communications: { payment_reminders: v } })} testid="comm-payment-reminders" />
                </SectionCard>

                {/* Subscription Automation */}
                <SectionCard icon={RefreshCw} title="Subscriptions" testid="sub-section">
                    <AutoRow label="Auto-renewal via mandate" hint="Charge mandate on renewal date instead of sending a manual payment link" checked={!!sub.auto_renewal} onChange={v => localPatch({ billing: { subscriptions: { auto_renewal: v } } })} testid="sub-auto-renewal" />
                    <AutoRow label="Dunning sequence" hint="Retry + reminder emails on payment failure (D+3, D+7, D+14)" checked={!!sub.dunning_enabled} onChange={v => localPatch({ billing: { subscriptions: { dunning_enabled: v } } })} testid="sub-dunning" />
                </SectionCard>

                {/* Invoicing Automation */}
                <SectionCard icon={FileText} title="Invoicing" testid="invoicing-section">
                    <AutoRow label="Monthly retainer auto-generation" hint="Generate retainer invoices on the 1st of every month for all retainer clients" checked={!!inv.monthly_retainer_auto_generate} onChange={v => localPatch({ billing: { invoicing: { monthly_retainer_auto_generate: v } } })} testid="inv-retainer" />
                    <AutoRow label="Post-hearing invoice draft" hint="Draft an appearance fee invoice the morning after each court hearing date" checked={!!inv.post_hearing_draft} onChange={v => localPatch({ billing: { invoicing: { post_hearing_draft: v } } })} testid="inv-post-hearing" />
                    <AutoRow label="Case closure final invoice" hint="Generate a final invoice when a case status changes to Disposed / Closed" checked={!!inv.case_closure_invoice} onChange={v => localPatch({ billing: { invoicing: { case_closure_invoice: v } } })} testid="inv-case-closure" />
                    <AutoRow label="Month-end time-to-invoice" hint="Compile all unbilled time entries into draft invoices at month end" checked={!!inv.month_end_time_to_invoice} onChange={v => localPatch({ billing: { invoicing: { month_end_time_to_invoice: v } } })} testid="inv-timesheet" />
                </SectionCard>

                {/* Collections */}
                <SectionCard icon={ShieldCheck} title="Collections" testid="collections-section">
                    <AutoRow label="Automated payment reminder sequence" hint="WhatsApp + email reminders at D+7, D+15, D+30 for outstanding invoices" checked={!!col.payment_reminder_sequence} onChange={v => localPatch({ billing: { collections: { payment_reminder_sequence: v } } })} testid="col-reminders" />
                    <AutoRow label="Auto write-off (requires partner approval)" hint="Flag invoices overdue > 90 days for write-off review" checked={!!col.auto_writeoff_enabled} onChange={v => localPatch({ billing: { collections: { auto_writeoff_enabled: v } } })} testid="col-writeoff" />
                </SectionCard>

                {/* Compliance */}
                <SectionCard icon={FileText} title="Compliance" testid="compliance-section">
                    <AutoRow label="GSTR-1 monthly auto-compilation" hint="Compile all invoice data into GSTR-1 format at month end" checked={!!comp.gstr1_auto_compile} onChange={v => localPatch({ billing: { compliance: { gstr1_auto_compile: v } } })} testid="comp-gstr1" />
                    <AutoRow label="E-invoice IRN auto-fetch" hint="Auto-generate IRN for invoices ≥ ₹5L via GSTN API" checked={!!comp.einvoice_auto_fetch} onChange={v => localPatch({ billing: { compliance: { einvoice_auto_fetch: v } } })} testid="comp-einvoice" />
                </SectionCard>

                {/* Save button */}
                <div className="flex justify-end pt-2">
                    <button onClick={saveAll} disabled={saving} data-testid="billing-auto-save"
                        className="px-5 py-2 bg-primary text-primary-foreground text-[10px] uppercase tracking-[0.16em] hover:opacity-90 disabled:opacity-50">
                        {saving ? "Saving…" : "Save all settings"}
                    </button>
                </div>
            </div>
        </>
    );
}

function deepMerge(base, override) {
    const result = { ...base };
    for (const k in override) {
        if (typeof override[k] === "object" && override[k] !== null && !Array.isArray(override[k]) && typeof result[k] === "object" && result[k] !== null) {
            result[k] = deepMerge(result[k], override[k]);
        } else {
            result[k] = override[k];
        }
    }
    return result;
}
